import { defineMessages, type IntlShape } from 'react-intl-next';

import type {
	BlockTransformContext,
	BlockTransformExtension,
	BlockTransformResult,
} from '@atlaskit/editor-common/block-menu/block-transform-extension';

import { getPanelColorForTabColor } from '../utils/tabColorTokens.ts';

const i18n = defineMessages({
	untitledTab: {
		id: 'native-tabs.block-transform.untitled-tab.ai-non-final',
		defaultMessage: 'Tab {number}',
		description:
			'Fallback title for an untitled tab in converted Panel or Layout content, or as an Expand title. {number} is replaced by the one-based tab position.',
	},
});

type TabMetadata = {
	color?: unknown;
	emoji?: string;
	title?: string;
};

type TransformAdf = BlockTransformResult['output'][number];

type TransformItem = {
	color?: string;
	content: TransformAdf[];
	emoji?: string;
	title: string;
};

// Panel accepts a narrower block set than Native Tabs frames, so known-invalid conversions stay
// hidden before Block Menu performs its final schema and insertion-position validation.
const panelContentTypes = new Set([
	'blockCard',
	'bulletList',
	'codeBlock',
	'decisionList',
	'extension',
	'heading',
	'mediaGroup',
	'mediaSingle',
	'orderedList',
	'paragraph',
	'rule',
	'taskList',
	'unsupportedBlock',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const isAdfEntity = (value: unknown): value is TransformAdf =>
	isRecord(value) && typeof value.type === 'string';

const isAdfContent = (content: unknown[]): content is TransformAdf[] => content.every(isAdfEntity);

const getTransformItems = (source: TransformAdf, intl: IntlShape): TransformItem[] | null => {
	if (source.type !== 'multiBodiedExtension') {
		return null;
	}

	const tabs = source.attrs?.parameters?.tabs;
	const frames = source.content;
	if (
		!Array.isArray(tabs) ||
		!Array.isArray(frames) ||
		tabs.length === 0 ||
		tabs.length !== frames.length
	) {
		return null;
	}

	const items: TransformItem[] = [];
	for (const [index, tab] of tabs.entries()) {
		if (!isRecord(tab)) {
			return null;
		}

		const frame = frames[index];
		if (!isAdfEntity(frame) || frame.type !== 'extensionFrame') {
			return null;
		}

		const frameContent = Array.isArray(frame.content) ? frame.content : [];
		if (!isAdfContent(frameContent)) {
			return null;
		}

		const metadata = tab as TabMetadata;
		const title =
			typeof metadata.title === 'string' && metadata.title.trim().length > 0
				? metadata.title
				: intl.formatMessage(i18n.untitledTab, { number: index + 1 });

		items.push({
			color: getPanelColorForTabColor(metadata.color),
			content: frameContent,
			emoji: typeof metadata.emoji === 'string' ? metadata.emoji : undefined,
			title,
		});
	}

	return items;
};

const createTitleParagraph = (title: string): TransformAdf => ({
	type: 'paragraph',
	content: [{ type: 'text', text: title }],
});

const createExpand = (item: TransformItem): TransformAdf => ({
	type: 'expand',
	attrs: {
		localId: crypto.randomUUID(),
		title: item.title,
	},
	content: item.content,
});

const createPanel = (item: TransformItem, targetTypeName: 'panel' | 'panel_c1'): TransformAdf => ({
	type: targetTypeName,
	attrs: {
		localId: crypto.randomUUID(),
		panelType: 'custom',
		...(item.color ? { panelColor: item.color } : {}),
		...(item.emoji
			? {
					panelIcon: item.emoji,
					panelIconId: item.emoji,
					panelIconText: item.emoji,
				}
			: {}),
	},
	content: [createTitleParagraph(item.title), ...item.content],
});

const createLayout = (items: TransformItem[]): TransformAdf => {
	const width = Number((100 / items.length).toFixed(2));
	return {
		type: 'layoutSection',
		content: items.map((item) => ({
			type: 'layoutColumn',
			attrs: { width },
			content: [createTitleParagraph(item.title), ...item.content],
		})),
	};
};

const toResult = (output: TransformAdf[]): BlockTransformResult | null =>
	output.length > 0
		? {
				output: output as [TransformAdf, ...TransformAdf[]],
			}
		: null;

const isPanelContentSupported = (item: TransformItem): boolean =>
	item.content.every((node) => panelContentTypes.has(node.type));

const isTargetSupported = (
	items: TransformItem[],
	targetTypeName: BlockTransformContext['targetTypeName'],
): boolean => {
	if (targetTypeName === 'layoutSection') {
		return items.length >= 2 && items.length <= 5;
	}

	if (targetTypeName === 'panel' || targetTypeName === 'panel_c1') {
		return items.every(isPanelContentSupported);
	}

	return targetTypeName === 'expand';
};

export const getNativeTabsBlockTransform = (intl: IntlShape): BlockTransformExtension => ({
	isSupported: ({ source, targetTypeName }) => {
		const items = getTransformItems(source, intl);
		if (!items) {
			return false;
		}

		return isTargetSupported(items, targetTypeName);
	},
	key: 'confluence-native-tabs',
	transform: ({ source, targetTypeName }) => {
		const items = getTransformItems(source, intl);
		if (!items || !isTargetSupported(items, targetTypeName)) {
			return null;
		}

		if (targetTypeName === 'expand') {
			return toResult(items.map(createExpand));
		}

		if (targetTypeName === 'panel' || targetTypeName === 'panel_c1') {
			return toResult(items.map((item) => createPanel(item, targetTypeName)));
		}

		if (targetTypeName === 'layoutSection') {
			return toResult([createLayout(items)]);
		}

		return null;
	},
});
